import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import analysisByPeriodVideoJa from '@/assets/images/top/ja/analysis-by-period.webm';
import keywordAnalysisVideoJa from '@/assets/images/top/ja/keyword-analysis.webm';
import activityCalendarVideoJa from '@/assets/images/top/ja/activity-calendar.webm';
import analysisByPeriodVideoEn from '@/assets/images/top/en/analysis-by-period.webm';
import keywordAnalysisVideoEn from '@/assets/images/top/en/keyword-analysis.webm';
import activityCalendarVideoEn from '@/assets/images/top/en/activity-calendar.webm';
import { Button } from '@/components/Button';
import searchCalendarIcon from '@/assets/images/common/search-calendar-icon.png';
import clsx from 'clsx';
import TopHeader from './TopHeader';
import { isJapaneseLocale } from '@/utils/isJapaneseLocale';

type VideoItem = {
    id: string;
    src: string;
    title: string;
};

function TopFirstView(): ReactElement {
    const { t, i18n } = useTranslation('top');
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const heroRef = useRef<HTMLDivElement>(null);
    const [showHeader, setShowHeader] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    // プリレンダリングの静的HTMLと初期描画を一致させるため、最初の2本を初期ロード
    const [loadedVideos, setLoadedVideos] = useState<Set<number>>(new Set([0, 1]));
    const badgeRefs = useRef<(HTMLDivElement | null)[]>([]);
    const catchphrase = t('hero.catchphrase') as string;
    const ctaButtonLabel = t('cta.buttonLabel');

    const videos: VideoItem[] = useMemo(() => {
        const ja = isJapaneseLocale(i18n.language);
        return [
            {
                id: 'analysisByPeriod',
                src: ja ? analysisByPeriodVideoJa : analysisByPeriodVideoEn,
                title: t('hero.videos.analysisByPeriod'),
            },
            {
                id: 'keywordAnalysis',
                src: ja ? keywordAnalysisVideoJa : keywordAnalysisVideoEn,
                title: t('hero.videos.keywordAnalysis'),
            },
            {
                id: 'activityCalendar',
                src: ja ? activityCalendarVideoJa : activityCalendarVideoEn,
                title: t('hero.videos.activityCalendar'),
            },
        ];
    }, [t, i18n.language]);

    useEffect(() => {
        setLoadedVideos(new Set([0, 1]));
        setActiveTabIndex(0);
    }, [i18n.language]);

    useEffect(() => {
        // 読み込まれた動画の再生速度を等速（1.0）に設定
        videoRefs.current.forEach((ref, index) => {
            if (ref && loadedVideos.has(index)) {
                ref.playbackRate = 2;
            }
        });
    }, [loadedVideos]);

    useEffect(() => {
        // 次の動画を事前に読み込む（現在の動画が再生中に）
        const nextIndex = (activeTabIndex + 1) % videos.length;
        if (!loadedVideos.has(nextIndex)) {
            setLoadedVideos((prev) => new Set([...prev, nextIndex]));
        }

        // アクティブな動画を再生
        videoRefs.current.forEach((ref, index) => {
            if (ref && loadedVideos.has(index)) {
                if (index === activeTabIndex) {
                    ref.play().catch(() => {
                        // 自動再生が失敗しても無視
                    });
                } else {
                    ref.pause();
                }
            }
        });
    }, [activeTabIndex, loadedVideos, videos]);

    const handleVideoEnded = () => {
        // 動画の再生終了時に次の動画へ切り替え
        setActiveTabIndex((prev) => (prev + 1) % videos.length);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (!heroRef.current) return;
            const rect = heroRef.current.getBoundingClientRect();
            setShowHeader(rect.bottom <= 0);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div className="flex flex-col items-center justify-start w-full bg-kotomilThemaColor pb-12 overflow-hidden relative">
            {/* Light Reflection Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none z-0"></div>

            {/* 最下部のゴールド→透明グラデーション（次のセクションとの繋ぎ） */}
            <div className="absolute bottom-0 left-0 w-full h-20 pointer-events-none z-0">
                <div className="absolute inset-0 bg-gradient-to-t from-transparent to-kotomilThemaColor"></div>
            </div>

            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-20 -right-20 w-96 h-96 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob"></div>
                <div className="absolute top-1/2 -left-20 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>
            </div>

            {/* Header Area (Logo) */}
            <TopHeader showHeader={showHeader} />

            <div
                ref={heroRef}
                className="w-full max-w-[1440px] mx-auto flex-1 flex flex-col items-center z-10 pt-12 lg:pt-24 lg:mt-0 px-4 md:px-6 lg:px-20"
            >
                <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-6 md:gap-8 lg:gap-12 mb-10 lg:mb-12">
                    {/* Copy */}
                    <div className="w-full lg:w-5/12 flex flex-col items-center lg:items-start text-center lg:text-left">
                        <div className="mb-6 lg:mb-6 w-full">
                            <h1 className="text-3xl md:text-4xl lg:text-[2.5rem] xl:text-[3rem] font-extrabold tracking-wide text-gray-900">
                                <span
                                    className="block text-kotomilThemaColorDarker leading-normal"
                                    dangerouslySetInnerHTML={{ __html: catchphrase }}
                                />
                            </h1>
                        </div>

                        {/* Feature Badges - 浮かび上がってくる */}
                        <div className="mb-8 lg:mb-10 lg:mt-8 w-full">
                            <div className="flex flex-col items-center lg:items-start gap-4 lg:gap-6">
                                {[
                                    { key: 'timeBias', delay: 0 },
                                    { key: 'effort', delay: 1000 },
                                    { key: 'myTime', delay: 2000 },
                                    { key: 'connection', delay: 3000 },
                                ].map((badge, index) => (
                                    <div
                                        key={badge.key}
                                        ref={(el) => {
                                            badgeRefs.current[index] = el;
                                        }}
                                        className="py-2 px-4 lg:py-2.5 lg:px-6 bg-white/40 backdrop-blur-sm border border-gray-900/10 rounded-xl opacity-100 animate-badgeFloat hover:bg-white/60 transition-all duration-300 cursor-default"
                                        style={{
                                            animationDelay: `${badge.delay}ms`,
                                        }}
                                    >
                                        <span className="text-base md:text-lg lg:text-xl xl:text-2xl font-bold tracking-wide text-gray-900 break-words">
                                            {t(`hero.badges.${badge.key}`)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Visual (Analysis Dashboard) */}
                    <div className="w-full lg:w-7/12 relative group mb-0">
                        {/* Browser Window Frame Effect */}
                        <div className="relative rounded-xl overflow-hidden shadow-xl bg-gray-700">
                            {/* Browser Header with Tabs */}
                            <div className="h-12 bg-gray-800 flex items-center px-4 gap-3 border-b border-gray-400">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-800"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-800"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-800"></div>
                                </div>
                                {/* Video Tabs */}
                                <div className="flex h-full items-end gap-2 ml-2 sm:ml-4 flex-1">
                                    {videos.map((video, index) => (
                                        <button
                                            key={video.id}
                                            onClick={() => {
                                                // タブクリック時に動画を読み込む
                                                if (!loadedVideos.has(index)) {
                                                    setLoadedVideos((prev) => new Set([...prev, index]));
                                                }
                                                setActiveTabIndex(index);
                                            }}
                                            className={clsx(
                                                'px-2 sm:px-3 py-1 rounded-t-lg text-[10px] sm:text-xs lg:text-sm transition-all duration-300',
                                                activeTabIndex === index
                                                    ? 'bg-kotomilThemaColor text-gray-900 font-bold hover:bg-gray-600 border-2 border-b-0 border-transparent'
                                                    : 'bg-gray-900 text-white border border-b-0 border-gray-50',
                                            )}
                                        >
                                            {video.title}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Video Container with Transition */}
                            <div className="relative w-full h-80 md:h-96 lg:h-[500px] bg-gray-600 overflow-hidden flex items-center justify-center">
                                {videos.map((video, index) => {
                                    // 読み込まれていない動画はレンダリングしない
                                    if (!loadedVideos.has(index)) {
                                        return null;
                                    }
                                    return (
                                        <video
                                            key={`${video.id}-${i18n.language}`}
                                            ref={(el) => {
                                                videoRefs.current[index] = el;
                                            }}
                                            src={video.src}
                                            muted
                                            playsInline
                                            preload={index === activeTabIndex ? 'auto' : 'none'}
                                            onEnded={handleVideoEnded}
                                            className={clsx(
                                                'absolute max-w-full max-h-full w-auto h-auto object-contain transition-opacity duration-500',
                                                activeTabIndex === index
                                                    ? 'opacity-100 z-10'
                                                    : 'opacity-0 z-0',
                                            )}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Button - テキストと動画の下、水平中央寄せ */}
                <div className="w-full max-w-md mx-auto">
                    <Button
                        variant="default"
                        className="w-full h-16 lg:h-20 px-4 md:px-6 lg:px-8 border-4 border-gray-900 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] rounded-xl text-gray-900
                        transition-all duration-200 ease-in-out
                        hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none
                        flex items-center justify-center gap-2 md:gap-4 group"
                        onClick={() => {
                            window.location.href = '/login';
                        }}
                    >
                        <span className="text-base md:text-xl lg:text-2xl font-bold tracking-widest">
                            {ctaButtonLabel}
                        </span>
                        <img
                            src={searchCalendarIcon}
                            alt=""
                            className="h-6 md:h-7 lg:h-8 w-auto group-hover:rotate-10 transition-transform duration-300"
                        />
                    </Button>

                    {/* Supported Calendars */}
                    <div className="mt-4 flex flex-col items-center gap-2">
                        <span className="text-[11px] lg:text-base text-gray-800">
                            {t('cta.supportedCalendars')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TopFirstView;
